<template>
  <Teleport to="body">
    <div
      v-if="isAffiliatePartnerModalVisible"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-[1250] p-0 sm:p-4"
      @click.self="closeAffiliatePartnerModal"
    >
      <div
        class="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden animate-modal-in max-h-[92vh] flex flex-col"
        role="dialog"
        :aria-labelledby="dialogTitleId"
        aria-modal="true"
      >
        <div class="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p
                v-if="hasPromoCode"
                class="text-xs font-semibold uppercase tracking-wide mb-1"
                :class="isSpacest ? 'text-emerald-600' : 'text-indigo-600'"
              >
                {{ promoCopy?.modal?.badge }}
              </p>
              <p
                v-else-if="isAya"
                class="text-xs font-semibold uppercase tracking-wide text-sky-600 mb-1"
              >
                {{ $t('affiliatePartnerModal.ayaBadge') }}
              </p>
              <p
                v-else
                class="text-xs font-semibold uppercase tracking-wide text-orange-600 mb-1"
              >
                {{ $t('affiliatePartnerModal.outsiteBadge') }}
              </p>
              <h2 :id="dialogTitleId" class="text-xl font-semibold text-gray-900">
                <template v-if="hasPromoCode">{{ promoCopy?.modal?.title }}</template>
                <template v-else-if="isAya">{{ $t('affiliatePartnerModal.ayaTitle') }}</template>
                <template v-else>{{ $t('affiliatePartnerModal.outsiteTitle') }}</template>
              </h2>
              <p class="text-sm text-gray-600 mt-1">
                <template v-if="hasPromoCode">{{ promoCopy?.savingsLabel }}</template>
                <template v-else-if="isAya">{{ $t('affiliatePartnerModal.ayaSubtitle', { title: listingTitle }) }}</template>
                <template v-else>{{ $t('affiliatePartnerModal.outsiteSubtitle', { title: listingTitle }) }}</template>
              </p>
            </div>
            <button
              type="button"
              class="p-2 rounded-full hover:bg-gray-100 text-gray-500 shrink-0"
              :aria-label="$t('affiliatePartnerModal.close')"
              @click="closeAffiliatePartnerModal"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form class="px-6 py-5 space-y-5 overflow-y-auto flex-1" @submit.prevent="handleContinue">
          <div v-if="hasPromoCode">
            <p class="text-sm text-gray-600 mb-2">
              {{ promoCopy?.modal?.useCodeText }}
            </p>
            <div class="flex items-stretch gap-2">
              <div
                class="flex-1 flex items-center justify-center px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-mono text-lg font-semibold tracking-wider text-gray-900 select-all"
              >
                {{ promoCopy?.code }}
              </div>
              <button
                type="button"
                class="px-4 py-3 rounded-xl text-sm font-medium transition-colors shrink-0"
                :class="[
                  copied
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : isSpacest ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-indigo-600 text-white hover:bg-indigo-700',
                  !canCopyOrContinue ? 'opacity-50 cursor-not-allowed' : '',
                ]"
                @click="copyCode"
              >
                {{ copied ? promoCopy?.modal?.copiedLabel : promoCopy?.modal?.copyLabel }}
              </button>
            </div>
            <p class="text-sm text-gray-500 leading-relaxed mt-3">
              {{ promoCopy?.modal?.footerText }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('scrapedLeadModal.labelEmail') }}
            </label>
            <input
              ref="emailInputRef"
              v-model="email"
              type="email"
              required
              autocomplete="email"
              :placeholder="$t('scrapedLeadModal.placeholderEmail')"
              class="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              :class="showEmailRequiredHint ? 'border-amber-400 ring-2 ring-amber-200' : 'border-gray-300'"
              @input="showEmailRequiredHint = false"
            />
            <p class="text-xs text-gray-500 mt-1.5">
              {{ $t('affiliatePartnerModal.emailHint') }}
            </p>
          </div>

          <label class="flex items-start gap-2 cursor-pointer">
            <input
              v-model="consent"
              type="checkbox"
              required
              class="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
            />
            <span class="text-xs text-gray-500 leading-relaxed">
              {{ $t('scrapedLeadModal.gdprText') }}
              <router-link to="/privacy-policy" target="_blank" class="text-indigo-500 hover:underline">
                {{ $t('scrapedLeadModal.gdprLink') }}
              </router-link>.
            </span>
          </label>

          <router-link
            v-if="isSpacest"
            :to="spacestShowcasePath"
            class="block text-center text-sm font-medium text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline"
            @click="closeAffiliatePartnerModal"
          >
            {{ $t('affiliatePartnerModal.spacestShowcaseLink') }}
          </router-link>

          <router-link
            v-if="isAya"
            :to="ayaShowcasePath"
            class="block text-center text-sm font-medium text-sky-700 hover:text-sky-900 underline-offset-2 hover:underline"
            @click="closeAffiliatePartnerModal"
          >
            {{ $t('affiliatePartnerModal.ayaShowcaseLink') }}
          </router-link>

          <div class="relative">
            <Transition name="affiliate-hint">
              <div
                v-if="continueHintMessage"
                role="status"
                class="absolute bottom-full left-1/2 z-10 mb-2 w-max max-w-[min(100%,18rem)] -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-center text-xs font-medium text-white shadow-lg"
              >
                {{ continueHintMessage }}
                <span
                  class="absolute left-1/2 top-full -translate-x-1/2 border-[6px] border-transparent border-t-gray-900"
                  aria-hidden="true"
                />
              </div>
            </Transition>
            <button
              type="submit"
              class="w-full py-3.5 px-4 text-white text-sm font-semibold rounded-xl transition-colors"
              :class="[
                isSpacest ? 'bg-emerald-700 hover:bg-emerald-800' : isAya ? 'bg-sky-700 hover:bg-sky-800' : 'bg-gray-900 hover:bg-gray-800',
                !canCopyOrContinue ? 'opacity-50 cursor-not-allowed' : '',
              ]"
            >
              <template v-if="hasPromoCode">{{ promoCopy?.modal?.ctaLabel }}</template>
              <template v-else-if="isAya">{{ $t('affiliatePartnerModal.continueAya') }}</template>
              <template v-else>{{ $t('affiliatePartnerModal.continueOutsite') }}</template>
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { getAuth } from 'firebase/auth';
import {
  getLeadSessionProfile,
  profileToFormDefaults,
  saveLeadSessionProfile,
} from '@/services/scrapedLeadSubmit';
import {
  isAffiliatePartnerModalVisible,
  affiliatePartnerColiving,
  affiliatePartnerKind,
  affiliateReferrerEmail,
  bluegroundPromoCopy,
  spacestPromoCopy,
  closeAffiliatePartnerModal,
  confirmAffiliatePartnerRedirect,
  notifyAffiliatePromoCopyOnce,
  spacestShowcasePath,
} from '@/utils/affiliatePartnerModal';
import { AYA_SHOWCASE_PATH } from '@/utils/ayaAffiliate';

const ayaShowcasePath = AYA_SHOWCASE_PATH;

const { t } = useI18n();

const email = ref('');
const consent = ref(false);
const copied = ref(false);
const emailInputRef = ref(null);
const showEmailRequiredHint = ref(false);
const continueHintMessage = ref('');
let continueHintTimer = null;

function isEmailValid() {
  const trimmed = email.value.trim();
  return trimmed.length > 3 && trimmed.includes('@');
}

function flashContinueHint(message) {
  continueHintMessage.value = message;
  if (continueHintTimer) clearTimeout(continueHintTimer);
  continueHintTimer = window.setTimeout(() => {
    continueHintMessage.value = '';
    continueHintTimer = null;
  }, 4000);
}

function requireFormBeforeAction() {
  if (!isEmailValid()) {
    showEmailRequiredHint.value = true;
    flashContinueHint(t('affiliatePartnerModal.emailRequiredToContinue'));
    nextTick(() => emailInputRef.value?.focus());
    return false;
  }
  if (!consent.value) {
    flashContinueHint(t('affiliatePartnerModal.consentRequiredToContinue'));
    return false;
  }
  return true;
}

const isBlueground = computed(() => affiliatePartnerKind.value === 'blueground');
const isSpacest = computed(() => affiliatePartnerKind.value === 'spacest');
const isAya = computed(() => affiliatePartnerKind.value === 'aya');
const hasPromoCode = computed(() => isBlueground.value || isSpacest.value);
const promoCopy = computed(() => {
  if (isSpacest.value) return spacestPromoCopy.value;
  return bluegroundPromoCopy.value;
});
const dialogTitleId = computed(() => {
  if (isBlueground.value) return 'affiliate-bg-title';
  if (isSpacest.value) return 'affiliate-spacest-title';
  if (isAya.value) return 'affiliate-aya-title';
  return 'affiliate-os-title';
});

const listingTitle = computed(() => {
  const c = affiliatePartnerColiving.value;
  return c?.title || c?.propertyTitle || '';
});

const canCopyOrContinue = computed(() => {
  const trimmed = email.value.trim();
  return consent.value && trimmed.length > 3 && trimmed.includes('@');
});

function applySessionAndAuthDefaults() {
  const sessionDefaults = profileToFormDefaults(getLeadSessionProfile());
  if (sessionDefaults?.applicantEmail) {
    email.value = sessionDefaults.applicantEmail;
    consent.value = sessionDefaults.consentLeadSharing === true;
  }
  const user = getAuth().currentUser;
  if (user?.email && !email.value) {
    email.value = user.email;
  }
}

watch(isAffiliatePartnerModalVisible, (visible) => {
  if (visible) {
    copied.value = false;
    email.value = '';
    consent.value = false;
    showEmailRequiredHint.value = false;
    continueHintMessage.value = '';
    if (continueHintTimer) {
      clearTimeout(continueHintTimer);
      continueHintTimer = null;
    }
    applySessionAndAuthDefaults();
    affiliateReferrerEmail.value = email.value;
  } else {
    email.value = '';
    consent.value = false;
    copied.value = false;
    showEmailRequiredHint.value = false;
    continueHintMessage.value = '';
  }
});

watch(email, (val) => {
  affiliateReferrerEmail.value = val;
});

function persistEmailForSession() {
  const normalized = email.value.trim().toLowerCase();
  if (!normalized || !consent.value) return;
  saveLeadSessionProfile({
    ...(getLeadSessionProfile() || {}),
    applicantEmail: normalized,
    consentLeadSharing: true,
  });
}

function handleContinue() {
  if (!requireFormBeforeAction()) return;
  affiliateReferrerEmail.value = email.value.trim();
  persistEmailForSession();
  confirmAffiliatePartnerRedirect();
}

async function copyCode() {
  if (!requireFormBeforeAction()) return;
  affiliateReferrerEmail.value = email.value.trim();
  persistEmailForSession();
  const code = promoCopy.value?.code;
  if (!code) return;

  function onCopySuccess() {
    copied.value = true;
    notifyAffiliatePromoCopyOnce();
    window.setTimeout(() => {
      copied.value = false;
    }, 2000);
  }

  try {
    await navigator.clipboard.writeText(code);
    onCopySuccess();
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = code;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    onCopySuccess();
  }
}
</script>

<style scoped>
.affiliate-hint-enter-active,
.affiliate-hint-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.affiliate-hint-enter-from,
.affiliate-hint-leave-to {
  opacity: 0;
}
</style>
